// File utilities for file/folder UI

// List of supported video formats
export const SUPPORTED_VIDEO_FORMATS = [
  "mp4",
  "mov",
  "avi",
  "mkv",
  "wmv",
  "flv",
  "webm",
  "m4v",
  "mpeg",
  "mpg",
  "3gp",
  "3g2",
  "h264",
  "rm",
  "swf",
  "vob",
  "ts",
  "ogv",
  "divx",
  "m2ts",
];

// List of supported audio formats
export const SUPPORTED_AUDIO_FORMATS = [
  "mp3",
  "wav",
  "ogg",
  "m4a",
  "aac",
  "wma",
  "flac",
  "alac",
  "aiff",
  "opus",
  "mid",
  "midi",
  "amr",
  "ac3",
  "au",
  "pcm",
  "ra",
  "rm",
  "ape",
];

export function getFileTypeIcon(fileOrName) {
  // If it's a folder, return folder icon
  if (typeof fileOrName === "object" && fileOrName?.type === "folder") {
    return "📁";
  }

  // Get the name from either the object or string
  const name = typeof fileOrName === "object" ? fileOrName?.name : fileOrName;

  // If no name is provided, return default icon
  if (!name) return "📄";

  try {
    const ext = name.split(".").pop().toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "bmp", "svg"].includes(ext)) return "🖼️";
    if (["pdf"].includes(ext)) return "📄";
    if (["doc", "docx"].includes(ext)) return "📝";
    if (["txt", "md"].includes(ext)) return "📃";
    if (["xls", "xlsx"].includes(ext)) return "📊";
    if (["ppt", "pptx"].includes(ext)) return "📈";
    if (SUPPORTED_VIDEO_FORMATS.includes(ext)) return "🎥";
    if (SUPPORTED_AUDIO_FORMATS.includes(ext)) return "🎵";
    return "📄";
  } catch (error) {
    return "📄"; // Return default icon if there's any error
  }
}

export function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

// Validate media file
export function isValidMediaFile(file) {
  if (!file) return false;

  // Check if it's a File object
  if (!(file instanceof File)) return false;

  // Get the file extension
  const extension = file.name.split(".").pop().toLowerCase();

  // Check if it's a supported format
  const isSupported =
    SUPPORTED_VIDEO_FORMATS.includes(extension) ||
    SUPPORTED_AUDIO_FORMATS.includes(extension);

  if (!isSupported) return false;

  // Check file size (limit to 500MB by default)
  const MAX_SIZE = 500 * 1024 * 1024; // 500MB in bytes
  if (file.size > MAX_SIZE) return false;

  return true;
}

// Get media file details
export function getMediaFileDetails(file) {
  const extension = file.name.split(".").pop().toLowerCase();
  const isVideo = SUPPORTED_VIDEO_FORMATS.includes(extension);
  const isAudio = SUPPORTED_AUDIO_FORMATS.includes(extension);

  return {
    name: file.name,
    size: formatFileSize(file.size),
    type: file.type,
    extension: extension,
    mediaType: isVideo ? "video" : isAudio ? "audio" : "unknown",
  };
}
